import { useSyncExternalStore } from "react";
import { RFI_KIND } from "@/lib/notifications";

/**
 * Tiny speech-output queue (window.speechSynthesis) shared by the BuiltBuddy bubbles — reads fresh RFI replies aloud
 * on a long-press. One queue at a time; starting a new one cancels the last. `useSpeaking()` reflects the state so the
 * bubble can show a speaker icon and a tap can stop it.
 */
const LANG = "en-AU";
const RATE = 1.02;

let speakingTag = ""; // "" | tag of the current queue
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
const setTag = (t) => {
  speakingTag = t;
  emit();
};

export const speechOutputSupported = () => typeof window !== "undefined" && !!window.speechSynthesis && typeof window.SpeechSynthesisUtterance === "function";

export const stopSpeaking = () => {
  try {
    window.speechSynthesis?.cancel();
  } catch {
    // unsupported
  }
  setTag("");
};

/**
 * Speak `lines` in order. Resolves with "done" (last line finished), "stopped" (cancelled by stopSpeaking / another
 * queue), "error" (the engine failed) or "unsupported" (no speechSynthesis / nothing to say). `tag` identifies the
 * queue for useSpeaking().
 */
export const speakLines = (lines, { tag = "speech", onEnd } = {}) =>
  new Promise((resolve) => {
    const synth = speechOutputSupported() ? window.speechSynthesis : null;
    const body = (lines || []).map((l) => String(l || "").trim()).filter(Boolean);
    if (!synth || !body.length) {
      onEnd?.("unsupported");
      resolve("unsupported");
      return;
    }
    let voice = null;
    try {
      synth.cancel();
      const voices = synth.getVoices() || [];
      voice = voices.find((v) => v.lang === LANG) || voices.find((v) => (v.lang || "").startsWith("en")) || null;
    } catch {
      // voices unavailable — the engine default is fine
    }
    let idx = 0;
    let finished = false;
    const finish = (status) => {
      if (finished) return;
      finished = true;
      if (speakingTag === tag) setTag("");
      onEnd?.(status);
      resolve(status);
    };
    const next = () => {
      if (speakingTag !== tag) return finish("stopped"); // cancelled by another queue / stopSpeaking
      if (idx >= body.length) return finish("done");
      try {
        const u = new SpeechSynthesisUtterance(body[idx++].slice(0, 1200));
        u.lang = LANG;
        u.rate = RATE;
        if (voice) u.voice = voice;
        u.onend = next;
        u.onerror = (e) => finish(speakingTag !== tag || e?.error === "interrupted" || e?.error === "canceled" ? "stopped" : "error");
        synth.speak(u);
      } catch {
        finish("error"); // engine refused — never let a read-out crash the page
      }
    };
    setTag(tag);
    next();
  });

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
export const useSpeaking = () => useSyncExternalStore(subscribe, () => speakingTag, () => "");

/** Same as useSpeaking() but true only for one queue tag. */
export const useSpeakingTag = (tag) => useSpeaking() === tag;

// ---- RFI replies → spoken lines ------------------------------------------------------------------------------------

const trimForSpeech = (s, n = 600) => {
  const t = String(s || "").replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t;
};

/**
 * Spoken script for fresh RFI replies (unread `rfi_answered` notification rows, newest first). Max 3 read in full;
 * the rest are counted. Older rows without meta.reply fall back to the one-line body.
 */
export const replyLines = (notes, { jobName = "", max = 3 } = {}) => {
  const rows = (notes || []).filter((n) => n.kind === RFI_KIND);
  if (!rows.length) return [];
  const many = rows.length > 1;
  const where = jobName ? ` on ${jobName}` : "";
  const lines = [`${many ? `${rows.length} new RFI replies` : "One new RFI reply"}${where}.`];
  rows.slice(0, max).forEach((n, i) => {
    const m = n.meta || {};
    const num = m.rfi_number || n.title?.split(" ")[0] || "an RFI";
    const who = m.consultant ? ` answered by ${m.consultant}` : "";
    const job = !jobName && n.job_name ? ` on ${n.job_name}` : "";
    const reply = trimForSpeech(m.reply) || trimForSpeech((n.body || "").split(" — ").slice(1).join(" — ")) || "";
    lines.push(`${many ? `Reply ${i + 1}. ` : ""}${num}${job}${who}${m.status === "closed" ? ", now closed" : ""}${reply ? `: ${reply}` : "."}`);
  });
  if (rows.length > max) lines.push(`Plus ${rows.length - max} more waiting in your notifications.`);
  lines.push(many ? "That's all the new replies." : "That's the new reply.");
  return lines;
};
